// ============================================================================
// LE FORMATEUR PARAMÉTRABLE — des écritures vers un fichier que le cabinet sait lire.
//
// POURQUOI IL EST PARAMÉTRABLE, ET CE QUE ÇA VEUT DIRE. Chaque éditeur comptable attend sa propre
// forme : séparateur, encodage, ordre des colonnes, débit et crédit en deux colonnes ou en un montant
// signé, virgule ou point décimal, date en huit chiffres ou avec des barres. Coder un exportateur par
// éditeur ferait de l'arrivée d'un cabinet une LIVRAISON — alors qu'un format manquant doit pouvoir
// s'ajouter par le support, sans mise à jour (décision produit 2026-07-29). Les préformats livrés
// (Sage, Cegid, EBP…) seront donc de simples jeux de paramètres d'ici, jamais du code.
//
// ⚠️ IL NE RECALCULE RIEN. Les montants, les sens et l'équilibre viennent du moteur (`buildEntry`) ;
// ce module ne fait que les METTRE EN FORME. Un formateur qui referait un calcul finirait par produire
// un fichier qui ne dit pas la même chose que l'écran qui l'a annoncé.
//
// ⚠️ CE QU'UN FORMAT NE PEUT PAS DÉFAIRE : l'ÉQUILIBRE de l'écriture. Σ débits = Σ crédits est une
// propriété comptable, pas une préférence — un fichier déséquilibré est refusé par tous les
// intégrateurs, et le rendre optionnel ne servirait qu'à produire des rejets. D'où `forDelivery`
// ci-dessous, qui est la SEULE porte d'entrée de ce module.
// ============================================================================
import type { EntryResult } from './index';

// ---------------------------------------------------------------------------
// LE VERROU — on ne formate pas une écriture, on formate une écriture REMETTABLE
// ---------------------------------------------------------------------------

declare const remettable: unique symbol;

/**
 * Des écritures qu'on a le DROIT de remettre à un cabinet.
 *
 * ⚠️ TYPE OPAQUE, ET C'EST TOUT L'INTÉRÊT. On ne peut pas en fabriquer une : seule `forDelivery` en
 * rend, et seulement après contrôle. Un format qui sauterait la vérification NE COMPILE PAS. La règle
 * « sans plan complet, on n'exporte pas » cesse donc d'être une consigne qu'on doit penser à appeler —
 * et une consigne de ce genre finit toujours oubliée au troisième format, c'est-à-dire au moment où
 * plus personne ne se souvient pourquoi elle existait.
 */
export interface DeliverableEntries {
  readonly [remettable]: true;
  readonly days: readonly EntryResult[];
  /** Empreinte du contenu — deux extractions identiques la partagent. Voir `empreinte`. */
  readonly fingerprint: string;
}

/** Ce qui interdit la remise, dit en clair et par motif. */
export interface ExportRefusal {
  kind: 'plan_incomplet' | 'desequilibre' | 'compte_manquant' | 'rien_a_remettre';
  /** Journée concernée (`AAAAMMJJ`), `null` quand le refus porte sur l'ensemble. */
  day: string | null;
  message: string;
}

/**
 * LA SEULE PORTE vers un fichier — elle contrôle, puis elle scelle.
 *
 * ⚠️ TROIS REFUS, ET ILS NE SE CORRIGENT PAS AU MÊME ENDROIT : un plan incomplet se remplit dans
 * l'onglet voisin, un compte manquant désigne un poste précis, un déséquilibre désigne une donnée de
 * caisse. Les fondre en « export impossible » priverait le lecteur de la seule information utile.
 *
 * ⚠️ UNE PÉRIODE SANS AUCUNE JOURNÉE EST UN REFUS, pas un fichier vide. Un fichier de zéro ligne
 * s'intègre sans erreur chez le cabinet et n'y laisse RIEN : le mois passerait pour traité.
 */
export function forDelivery(
  days: readonly EntryResult[],
  planComplet: boolean,
): { ok: true; entries: DeliverableEntries } | { ok: false; refusal: ExportRefusal[] } {
  const refusal: ExportRefusal[] = [];

  if (!planComplet) {
    refusal.push({
      kind: 'plan_incomplet', day: null,
      message: "Le plan comptable n'est pas complet : des postes n'ont pas de compte validé. "
        + 'Un export partiel produirait des écritures fausses qu’un cabinet intégrerait.',
    });
  }
  if (days.length === 0) {
    refusal.push({
      kind: 'rien_a_remettre', day: null,
      message: 'Cette période ne contient aucune journée à écrire — il n’y a rien à remettre.',
    });
  }
  for (const d of days) {
    for (const p of d.problems) {
      refusal.push({
        kind: p.kind === 'unbalanced' ? 'desequilibre' : 'compte_manquant',
        day: d.provenance.month,
        message: `${d.provenance.month} — ${p.message}`,
      });
    }
  }

  if (refusal.length > 0) return { ok: false, refusal };
  // ⚠️ LA MARQUE N'EXISTE QU'AU TYPAGE : `remettable` est un `unique symbol` déclaré, il n'a AUCUNE
  //    valeur à l'exécution. On ne peut donc pas l'écrire comme clé — la conversion la pose au niveau
  //    des types, ce qui suffit : personne d'autre ne peut fabriquer cette forme, et le fichier
  //    produit ne traîne aucun champ parasite.
  return { ok: true, entries: { days, fingerprint: empreinte(days) } as unknown as DeliverableEntries };
}

/**
 * EMPREINTE D'UN JEU D'ÉCRITURES — pour dire « identique au précédent » lors d'un rejeu.
 *
 * ⚠️ ELLE RÉPOND À UNE SEULE QUESTION, et c'est délibéré : *est-ce le même fichier ?*, pas *qu'est-ce
 * qui a changé ?*. Le cas utile n'est pas la correction d'un compte — là, on SAIT que ça change ; c'est
 * le rejeu par doute ou après un fichier perdu, où « identique, vous pouvez remettre le même sans
 * risque » évite un appel au cabinet. Conserver l'intégralité des lignes pour construire un
 * comparateur serait de la machinerie pour une question que personne ne pose (arbitrage 2026-08-23).
 *
 * ⚠️ ELLE NE PORTE QUE LE CONTENU COMPTABLE — jamais l'instant d'extraction, sinon deux extractions
 * du même mois inchangé auraient deux empreintes et l'indication s'inverserait.
 * ⚠️ SOMME NON CRYPTOGRAPHIQUE, ASSUMÉE : elle sert d'indice à l'écran, elle ne scelle rien. Le
 * scellement fiscal, lui, est signé par la caisse et vit ailleurs.
 */
export function empreinte(days: readonly EntryResult[]): string {
  const texte = days.map((d) => [
    d.provenance.month, d.journal,
    ...d.lines.map((l) => `${l.account}|${l.label}|${l.debitCents}|${l.creditCents}`),
  ].join('\n')).join('\n');
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < texte.length; i++) {
    const c = texte.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 + c, 0x85ebca6b) >>> 0;
  }
  return `${h1.toString(16).padStart(8, '0')}${h2.toString(16).padStart(8, '0')}`;
}

// ---------------------------------------------------------------------------
// LA SPÉCIFICATION D'UN FORMAT
// ---------------------------------------------------------------------------

/** Séparateur de colonnes. ⚠️ `fixed` = largeur fixe : chaque colonne doit alors porter sa largeur. */
export type Separateur = ';' | ',' | '\t' | '|' | 'fixed';

/**
 * Encodage du fichier.
 * ⚠️ `windows-1252` N'EST PAS UN VESTIGE : beaucoup d'outils comptables français l'attendent encore, et
 * leur livrer de l'UTF-8 sans marque d'ordre casse les accents EN SILENCE — le fichier s'intègre, et
 * « Société » devient « SociÃ©tÃ© » dans le grand livre.
 */
export type Encodage = 'utf-8' | 'utf-8-bom' | 'windows-1252';

/** Ce qu'une colonne rend. */
export type ChampExport =
  | 'journal' | 'date' | 'compte' | 'libelle'
  | 'debit' | 'credit' | 'montant' | 'sens' | 'devise';

/** Comment le sens de la ligne s'écrit — les trois conventions du marché. */
export type ConventionSens =
  /** Deux colonnes `debit` et `credit`, l'une vide. */
  | 'colonnes'
  /** Une colonne `montant`, négative au crédit. */
  | 'signe'
  /** Une colonne `montant` en valeur absolue + une colonne `sens` (`D`/`C`). */
  | 'lettre';

/**
 * LES REPÈRES D'UN MASQUE DE DATE — l'alphabet que ce moteur reconnaît, écrit UNE fois.
 * ⚠️ Ils sont FRANÇAIS (A comme année, M comme mois, J comme jour) et le RESTENT : un masque est une
 *    valeur PERSISTÉE dans `FormatSpec.masqueDate`, relue par ce contrôle. Les traduire refuserait
 *    tous les formats déjà enregistrés — voir la note au-dessus de l'interface.
 */
export const DATE_MASK_MARKERS = /AAAA|AA|MM|JJ/;

/**
 * LES MASQUES PROPOSÉS À L'ÉCRAN — des RACCOURCIS, jamais une liste fermée : le champ reste libre.
 * ⚠️ ILS VIVENT ICI, PAS DANS L'ÉCRAN, parce que c'est ce moteur qui décide de ce qu'un masque peut
 *    contenir. Un écran qui les écrit lui-même finit par proposer ce que le contrôle refuse — et les
 *    outils de traduction les prendraient pour des textes, ce qui les casserait en silence.
 */
export const DATE_MASK_SUGGESTIONS = ['JJ/MM/AAAA', 'JJMMAAAA', 'AAAAMMJJ', 'JJ-MM-AA', 'JJ.MM.AAAA'] as const;

export interface ColonneSpec {
  champ: ChampExport;
  /** En-tête, quand la ligne d'en-tête est demandée. */
  heading: string;
  /** Largeur — OBLIGATOIRE en largeur fixe, ignorée sinon. */
  largeur?: number;
}

/**
 * Tout ce qui fait un format, et rien qui ne soit un choix du cabinet.
 *
 * ⚠️ CONTRAT PERSISTÉ — SES NOMS DE CHAMPS SONT DE LA DONNÉE, PAS DES IDENTIFIANTS (2026-08-26).
 * Un `FormatSpec` est **sérialisé et stocké** (colonne `spec`, relu par `readFormatSpec`) : chaque
 * format que le cabinet a configuré vit en base sous CES noms. Les renommer — même vers un anglais
 * plus cohérent — ferait lire `undefined` sur tous les formats déjà enregistrés, en silence, et
 * TypeScript ne verrait rien puisque la relecture part d'une chaîne. C'est la raison, et la SEULE,
 * pour laquelle `enTete` et `masqueDate` échappent à la règle de langue (CLAUDE.md) : les traduire
 * demande une MIGRATION des specs stockées, pas un renommage. Tenté puis annulé le 2026-08-26.
 */
export interface FormatSpec {
  nom: string;
  separateur: Separateur;
  encodage: Encodage;
  /** `csv` · `txt` — sans point. */
  extension: string;
  enTete: boolean;
  /** Masque de date : `AAAA` `AA` `MM` `JJ`, tout le reste littéral (`JJ/MM/AAAA`, `AAAAMMJJ`…). */
  masqueDate: string;
  // (les repères et les masques proposés vivent sous cette interface — voir `DATE_MASK_MARKERS`)
  decimale: ',' | '.';
  sens: ConventionSens;
  colonnes: readonly ColonneSpec[];
  /** ⚠️ `CRLF` par défaut du marché : plusieurs intégrateurs français refusent un fichier en `LF` seul. */
  finDeLigne: '\r\n' | '\n';
  /** Lettre de la devise, quand une colonne `devise` est demandée. */
  devise: string;
}

/** Ce qui empêche un format de produire un fichier — une faute de PARAMÉTRAGE, pas de données. */
export interface DefautDeFormat {
  champ: string;
  message: string;
}

/**
 * UN FORMAT EST-IL COHÉRENT ? — contrôlé AVANT de produire quoi que ce soit.
 *
 * ⚠️ CE SONT DES INCOHÉRENCES SILENCIEUSES qu'on attrape ici. Un format en largeur fixe sans largeurs
 * produit un fichier illisible sans lever la moindre erreur ; une convention « deux colonnes » sans
 * colonne crédit perd la moitié de l'écriture et le fichier reste bien formé. Dans les deux cas le
 * cabinet découvre le problème après intégration.
 */
export function defautsDuFormat(spec: FormatSpec): DefautDeFormat[] {
  const d: DefautDeFormat[] = [];
  if (spec.colonnes.length === 0) d.push({ champ: 'colonnes', message: 'Un format sans colonne ne produit rien.' });

  const champs = new Set(spec.colonnes.map((c) => c.champ));
  if (spec.sens === 'colonnes' && !(champs.has('debit') && champs.has('credit'))) {
    d.push({ champ: 'sens', message: 'La convention « deux colonnes » exige une colonne Débit ET une colonne Crédit.' });
  }
  if (spec.sens !== 'colonnes' && !champs.has('montant')) {
    d.push({ champ: 'sens', message: 'Cette convention exige une colonne Montant.' });
  }
  if (spec.sens === 'lettre' && !champs.has('sens')) {
    d.push({ champ: 'sens', message: 'La convention « lettre » exige une colonne Sens (D/C).' });
  }
  if (spec.sens !== 'colonnes' && (champs.has('debit') || champs.has('credit'))) {
    d.push({ champ: 'colonnes', message: 'Colonnes Débit/Crédit et colonne Montant ne se combinent pas : le montant serait écrit deux fois.' });
  }
  if (spec.separateur === 'fixed') {
    for (const c of spec.colonnes) {
      if (!c.largeur || c.largeur < 1) {
        d.push({ champ: `colonnes.${c.champ}`, message: `La colonne « ${c.heading} » n'a pas de largeur, obligatoire en largeur fixe.` });
      }
    }
  }
  if (!DATE_MASK_MARKERS.test(spec.masqueDate)) {
    d.push({ champ: 'masqueDate', message: `Le masque « ${spec.masqueDate} » ne contient aucun repère de date (AAAA, AA, MM, JJ).` });
  }
  // ⚠️ Un séparateur décimal identique au séparateur de colonnes coupe chaque montant en deux.
  if (spec.separateur === spec.decimale) {
    d.push({ champ: 'decimale', message: 'Le séparateur décimal ne peut pas être le séparateur de colonnes : chaque montant serait coupé en deux.' });
  }
  return d;
}

// ---------------------------------------------------------------------------
// LES CONVERSIONS — chaînes vers chaînes, jamais d'objet intermédiaire
// ---------------------------------------------------------------------------

/**
 * LA DATE AU MASQUE — transformation de CHAÎNE, jamais un objet `Date`.
 *
 * ⚠️ LA SOURCE EST DÉJÀ UNE DATE LOCALE (`AAAAMMJJ`, journée fiscale décidée par la caisse). La faire
 * passer par un `Date` réintroduirait le fuseau horaire qu'on a mis deux passes à éliminer — et un
 * décalage d'un jour sur un export comptable déplace des écritures d'un exercice à l'autre.
 *
 * ⚠️ `AAAA` AVANT `AA` : l'ordre de substitution n'est pas cosmétique. Traiter `AA` d'abord laisserait
 * « 26 26 » là où le masque demandait « 2026 ».
 */
export function formatDateWithMask(ymd: string, mask: string): string {
  const a = ymd.slice(0, 4);
  const m = ymd.slice(4, 6);
  const j = ymd.slice(6, 8);
  return mask.replace(/AAAA|AA|MM|JJ/g, (t) => (t === 'AAAA' ? a : t === 'AA' ? a.slice(2) : t === 'MM' ? m : j));
}

/**
 * UN MONTANT EN CENTIMES vers sa forme fichier.
 *
 * ⚠️ TOUJOURS DEUX DÉCIMALES, même sur un compte rond : « 1250 » et « 1250,00 » ne se lisent pas
 * pareil selon l'intégrateur, et certains prennent le premier pour des centimes.
 * ⚠️ AUCUN SÉPARATEUR DE MILLIERS. Il n'est pas paramétrable, et ce n'est pas un oubli : dans un
 * fichier délimité il coupe le montant en deux si le délimiteur est la virgule, et aucun intégrateur
 * comptable n'en attend. Ce qui se lit à l'écran et ce qui s'écrit dans un fichier n'ont pas les mêmes
 * règles.
 */
export function montantAuFormat(cents: number, decimale: ',' | '.'): string {
  const signe = cents < 0 ? '-' : '';
  const v = Math.abs(cents);
  return `${signe}${Math.floor(v / 100)}${decimale}${String(v % 100).padStart(2, '0')}`;
}

/** Échappe une valeur pour un fichier délimité — guillemets doublés, entourage si nécessaire. */
function echapper(valeur: string, separateur: string): string {
  if (!valeur.includes(separateur) && !valeur.includes('"') && !valeur.includes('\n') && !valeur.includes('\r')) {
    return valeur;
  }
  return `"${valeur.replace(/"/g, '""')}"`;
}

/**
 * Cale une valeur à la largeur voulue.
 *
 * ⚠️ ON NE TRONQUE JAMAIS UN NOMBRE. Couper un libellé trop long est une gêne ; couper « 1250,00 » en
 * « 1250,0 » est une écriture FAUSSE qui s'intègre sans erreur. Le refus remonte donc comme un défaut
 * de format, à corriger dans les largeurs.
 */
function caler(valeur: string, largeur: number, numerique: boolean): { texte: string; trop: boolean } {
  if (valeur.length <= largeur) {
    return { texte: numerique ? valeur.padStart(largeur, ' ') : valeur.padEnd(largeur, ' '), trop: false };
  }
  return { texte: numerique ? valeur : valeur.slice(0, largeur), trop: numerique };
}

// ---------------------------------------------------------------------------
// LE RENDU
// ---------------------------------------------------------------------------

/** Le fichier produit, et ce qu'il a fallu concéder pour le produire. */
export interface FichierExport {
  /** Contenu en texte — l'encodage en octets est une étape à part (`encoder`). */
  texte: string;
  /** Nombre de lignes d'écriture rendues (hors en-tête). */
  rows: number;
  /** Empreinte du contenu comptable — reprise de la remise, pour l'historique. */
  fingerprint: string;
  /** Défauts qui ont empêché un rendu fidèle (largeur trop courte sur un nombre…). */
  defauts: DefautDeFormat[];
}

const CHAMPS_NUMERIQUES = new Set<ChampExport>(['debit', 'credit', 'montant']);

/**
 * MET LES ÉCRITURES EN FORME — la fonction que le canal fichier appelle.
 *
 * ⚠️ ELLE N'ACCEPTE QU'UNE ÉCRITURE REMETTABLE (`DeliverableEntries`), et c'est le verrou : on ne peut
 * pas lui passer une écriture qui n'a pas franchi `forDelivery`.
 *
 * ⚠️ L'ORDRE EST CELUI DES JOURNÉES, puis celui des lignes du moteur. Deux extractions du même mois
 * produisent donc le même fichier, octet pour octet — c'est ce qui permet à un cabinet de comparer un
 * ré-export au précédent, et c'est ce que l'empreinte constate.
 */
export function formatEntries(entries: DeliverableEntries, spec: FormatSpec): FichierExport {
  const defauts = defautsDuFormat(spec);
  if (defauts.length > 0) return { texte: '', rows: 0, fingerprint: entries.fingerprint, defauts };

  const sep = spec.separateur === 'fixed' ? '' : spec.separateur;
  const rendus: string[] = [];
  const soucis: DefautDeFormat[] = [];

  const poser = (values: readonly { champ: ChampExport | null; texte: string }[]): string => {
    const cellules = values.map((v, i) => {
      const col = spec.colonnes[i]!;
      if (spec.separateur === 'fixed') {
        const num = v.champ !== null && CHAMPS_NUMERIQUES.has(v.champ);
        const { texte, trop } = caler(v.texte, col.largeur ?? 0, num);
        if (trop) {
          soucis.push({
            champ: `colonnes.${col.champ}`,
            message: `« ${col.heading} » : ${col.largeur} caractères ne suffisent pas pour ${v.texte} — un nombre ne se tronque pas.`,
          });
        }
        return texte;
      }
      return echapper(v.texte, sep);
    });
    return cellules.join(sep);
  };

  if (spec.enTete) {
    rendus.push(poser(spec.colonnes.map((c) => ({ champ: null, texte: c.heading }))));
  }

  let rows = 0;
  for (const d of entries.days) {
    const date = formatDateWithMask(d.provenance.month, spec.masqueDate);
    for (const l of d.lines) {
      const auDebit = l.debitCents > 0;
      const montant = auDebit ? l.debitCents : l.creditCents;
      const values = spec.colonnes.map((c) => {
        switch (c.champ) {
          case 'journal': return { champ: c.champ, texte: d.journal };
          case 'date': return { champ: c.champ, texte: date };
          case 'compte': return { champ: c.champ, texte: l.account };
          case 'libelle': return { champ: c.champ, texte: l.label };
          case 'debit': return { champ: c.champ, texte: auDebit ? montantAuFormat(l.debitCents, spec.decimale) : '' };
          case 'credit': return { champ: c.champ, texte: auDebit ? '' : montantAuFormat(l.creditCents, spec.decimale) };
          case 'montant': return {
            champ: c.champ,
            // ⚠️ En convention `signe`, le crédit est NÉGATIF ; en `lettre`, c'est la colonne Sens qui
            //    porte l'information et le montant reste positif. Mélanger les deux doublerait le signe.
            texte: montantAuFormat(spec.sens === 'signe' && !auDebit ? -montant : montant, spec.decimale),
          };
          case 'sens': return { champ: c.champ, texte: auDebit ? 'D' : 'C' };
          case 'devise': return { champ: c.champ, texte: spec.devise };
          default: return { champ: c.champ, texte: '' };
        }
      });
      rendus.push(poser(values));
      rows++;
    }
  }

  return {
    // ⚠️ FIN DE LIGNE FINALE INCLUSE : plusieurs intégrateurs ignorent la dernière ligne d'un fichier
    //    qui ne se termine pas par un saut — et c'est le règlement en espèces qui disparaît.
    texte: rendus.join(spec.finDeLigne) + (rendus.length > 0 ? spec.finDeLigne : ''),
    rows,
    fingerprint: entries.fingerprint,
    defauts: soucis,
  };
}

// ---------------------------------------------------------------------------
// L'ENCODAGE
// ---------------------------------------------------------------------------

/**
 * Les caractères de Windows-1252 hors Latin-1 (plage 0x80–0x9F) — ceux qui n'ont PAS le même code
 * qu'en Unicode. ⚠️ L'apostrophe typographique « ’ » en fait partie, et elle est partout dans nos
 * libellés français : sans cette table, chaque « d'écart » deviendrait un point d'interrogation.
 */
const CP1252_HAUT: Readonly<Record<number, number>> = {
  0x20ac: 0x80, 0x201a: 0x82, 0x0192: 0x83, 0x201e: 0x84, 0x2026: 0x85, 0x2020: 0x86, 0x2021: 0x87,
  0x02c6: 0x88, 0x2030: 0x89, 0x0160: 0x8a, 0x2039: 0x8b, 0x0152: 0x8c, 0x017d: 0x8e, 0x2018: 0x91,
  0x2019: 0x92, 0x201c: 0x93, 0x201d: 0x94, 0x2022: 0x95, 0x2013: 0x96, 0x2014: 0x97, 0x02dc: 0x98,
  0x2122: 0x99, 0x0161: 0x9a, 0x203a: 0x9b, 0x0153: 0x9c, 0x017e: 0x9e, 0x0178: 0x9f,
};

/** Le fichier en octets, et ce que l'encodage a coûté. */
export interface OctetsExport {
  octets: Uint8Array;
  /** Caractères qui n'existent pas dans l'encodage choisi et ont été remplacés par `?`. */
  remplaces: number;
}

/**
 * ENCODE LE FICHIER — étape SÉPARÉE du rendu, parce que ce sont deux décisions distinctes.
 *
 * ⚠️ LE REMPLACEMENT SE COMPTE ET SE DIT. Un caractère absent de Windows-1252 devient `?` ; le taire
 * ferait partir un libellé mutilé sans que personne ne le sache, et c'est exactement le genre de
 * dégât qu'on ne découvre que dans le grand livre du cabinet.
 *
 * ⚠️ LA MARQUE D'ORDRE (BOM) N'EST PAS UNE COQUETTERIE : sans elle, un tableur français ouvre un
 * fichier UTF-8 en Windows-1252 et affiche « SociÃ©tÃ© ». Avec elle, certains intégrateurs plus anciens
 * lisent trois octets parasites en tête de première colonne. D'où le choix explicite, jamais un défaut.
 */
export function encoder(texte: string, encodage: Encodage): OctetsExport {
  if (encodage === 'windows-1252') {
    const out = new Uint8Array(texte.length);
    let remplaces = 0;
    for (let i = 0; i < texte.length; i++) {
      const c = texte.charCodeAt(i);
      if (c <= 0xff && !(c >= 0x80 && c <= 0x9f)) out[i] = c;
      else if (CP1252_HAUT[c] !== undefined) out[i] = CP1252_HAUT[c]!;
      else { out[i] = 0x3f; remplaces++; }
    }
    return { octets: out, remplaces };
  }

  const utf8 = utf8Octets(texte);
  if (encodage === 'utf-8-bom') {
    const avecBom = new Uint8Array(utf8.length + 3);
    avecBom.set([0xef, 0xbb, 0xbf], 0);
    avecBom.set(utf8, 3);
    return { octets: avecBom, remplaces: 0 };
  }
  return { octets: utf8, remplaces: 0 };
}

/** UTF-8 à la main — ⚠️ `TextEncoder` n'existe pas partout où ce cœur tourne (Hermes, tests Node anciens). */
function utf8Octets(s: string): Uint8Array {
  const out: number[] = [];
  for (const ch of s) {
    const c = ch.codePointAt(0)!;
    if (c < 0x80) out.push(c);
    else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
    else if (c < 0x10000) out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
    else {
      out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 0x3f), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
    }
  }
  return Uint8Array.from(out);
}

// ---------------------------------------------------------------------------
// LE FORMAT PAR DÉFAUT
// ---------------------------------------------------------------------------

/**
 * Le format livré avec le produit — celui qui permet d'exporter le jour de l'ouverture.
 *
 * ⚠️ IL VIT DANS LE CODE, PAS EN BASE, et c'est délibéré. Un magasin doit pouvoir produire un fichier
 * sans qu'on ait eu à semer une ligne pour lui : semer, c'est une écriture de plus à faire réussir au
 * provisioning, et une ligne de moins le jour où elle a échoué sans qu'on le voie. Le dupliquer crée
 * un format sur mesure, qui atterrit en base.
 *
 * ⚠️ SES VALEURS SONT CELLES QUI CASSENT LE MOINS, pas celles qui plaisent le plus : point-virgule
 * (un CSV français), UTF-8 avec marque d'ordre (les tableurs français ouvrent l'UTF-8 nu en 1252 et
 * affichent « SociÃ©tÃ© »), CRLF (plusieurs intégrateurs refusent le LF seul), virgule décimale, deux
 * colonnes débit/crédit (la convention la plus répandue). Un cabinet qui veut autre chose duplique.
 *
 * ⚠️ NON MODIFIABLE À L'ÉCRAN : c'est du code, et le laisser éditer donnerait un réglage qui
 * disparaîtrait à la livraison suivante.
 */
export const FORMAT_PAR_DEFAUT: FormatSpec = {
  nom: 'CSV standard Orgazone',
  separateur: ';',
  encodage: 'utf-8-bom',
  extension: 'csv',
  enTete: true,
  masqueDate: 'JJ/MM/AAAA',
  decimale: ',',
  sens: 'colonnes',
  finDeLigne: '\r\n',
  devise: 'EUR',
  colonnes: [
    { champ: 'date', heading: 'Date' },
    { champ: 'journal', heading: 'Journal' },
    { champ: 'compte', heading: 'Compte' },
    { champ: 'libelle', heading: 'Libellé' },
    { champ: 'debit', heading: 'Débit' },
    { champ: 'credit', heading: 'Crédit' },
  ],
};

/** Code réservé du format livré — aucun format sur mesure ne peut le porter. */
export const CODE_FORMAT_PAR_DEFAUT = 'ORGAZONE-CSV';

/**
 * Un `FormatSpec` relu depuis sa forme stockée, ou la raison du refus.
 *
 * ⚠️ ON NE FAIT JAMAIS CONFIANCE À DU JSON STOCKÉ. Il a pu être écrit par une version antérieure, ou
 * par une reprise depuis un magasin dont le format a évolué. Un `JSON.parse` suivi d'une conversion de
 * type laisserait passer un format sans colonnes, qui produirait un fichier vide *sans erreur*.
 */
export function readFormatSpec(json: string): { ok: true; spec: FormatSpec } | { ok: false; reason: string } {
  let brut: unknown;
  try {
    brut = JSON.parse(json);
  } catch {
    return { ok: false, reason: 'format illisible (JSON invalide)' };
  }
  if (typeof brut !== 'object' || brut === null) return { ok: false, reason: 'format illisible (objet attendu)' };

  const o = brut as Record<string, unknown>;
  const colonnes = Array.isArray(o.colonnes) ? o.colonnes : null;
  if (!colonnes) return { ok: false, reason: 'format sans liste de colonnes' };

  const spec: FormatSpec = {
    nom: String(o.nom ?? ''),
    separateur: (o.separateur ?? ';') as Separateur,
    encodage: (o.encodage ?? 'utf-8') as Encodage,
    extension: String(o.extension ?? 'csv'),
    enTete: o.enTete !== false,
    masqueDate: String(o.masqueDate ?? ''),
    decimale: (o.decimale === '.' ? '.' : ',') as ',' | '.',
    sens: (o.sens ?? 'colonnes') as ConventionSens,
    finDeLigne: o.finDeLigne === '\n' ? '\n' : '\r\n',
    devise: String(o.devise ?? 'EUR'),
    colonnes: colonnes.map((c) => {
      const x = (c ?? {}) as Record<string, unknown>;
      return {
        champ: x.champ as ChampExport,
        heading: String(x.heading ?? ''),
        ...(typeof x.largeur === 'number' ? { largeur: x.largeur } : null),
      };
    }),
  };

  // ⚠️ LE MÊME CONTRÔLE QU'À LA SAISIE, et pas un contrôle allégé « puisque c'était déjà validé » :
  //    ce qui a été validé l'a été par une version du logiciel, pas par celle qui lit aujourd'hui.
  const defauts = defautsDuFormat(spec);
  if (defauts.length > 0) return { ok: false, reason: defauts.map((d) => d.message).join(' · ') };
  return { ok: true, spec };
}
